import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';

import {
  AssessmentQuestion,
  bodyComposition,
  RemoteConfigVariables,
  ScanProgress } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { getApiConfig, getUrls } from '../config';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme from '../theme';
import { toggleBodyScanModal } from '../ui/redux';
import {UserDataConsumer} from '../utils';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { FirebaseRemoteConfig } from '../utils/FirebaseRemoteConfig';
import { openInAppWebViewerInBackground } from '../utils/inAppWebViewer';
import { defaultScale } from '../utils/scaling';
import { continueToSetYourWeightTargetButtonPressed } from '../WeightTarget/actions';
import {
  discardBodyScaleScan,
  doBodyAssessment,
  getNextBodyAssessmentQuestion,
  getPreviousBodyAssessmentQuestion, IBodyScanError,
  overrideBodyCompositionAndConditioning,
  removeBodyScale,
  saveBodyAssessment,
  saveBodyScaleScan,
  setBodyAssessmentAnswer,
  setScanProgress,
  showBodyTypeOptions,
  startBodyScaleScan,
  startBodyScaleScanSetup,
} from './actions';
import {
  BodyscaleAssessment,
  BodyscaleAssessmentSummary,
  BodyscaleBodyTypes,
  BodyscaleOnboarding,
  BodyscaleScanError,
  BodyscaleScanProgress,
  BodyscaleScanSummary,
  BodyscaleStartScan,
} from './components';
import { RemoveBodyScale } from './components/remove-bodyscale';
import {
  bodyScanStatsGaugeSelector,
  getQuestionStep,
  selectAthleteMode,
  selectIsRemovingBodyScale,
  selectIsReviewingTargetAndSavingScan,
  selectIsScanContinuingFromWeightTarget,
} from './selectors';
interface IProps {
  userProfile: UserProfile;
  bodyscanModalVisible: boolean;
  scaleScanStats: any;
  isBodyScaleScanStarted: boolean;
  progress: ScanProgress;
  phase: string;
  error: IBodyScanError;
  isSaving: boolean;
  metrics: any;
  questionsAndAnswers: AssessmentQuestion[];
  currentQuestion: AssessmentQuestion;
  currentQuestionAnswers: AssessmentQuestion[];
  questionStep: number;
  composition: bodyComposition;
  from: ScanProgress;
  athleteMode: boolean;
  isScanContinuingFromWeightTarget: boolean;
  isReviewingTargetAndSavingScan: boolean;
  isRemovingBodyScale: boolean;
  t: (key: string) => string;
  startScanSetup(userProfile: UserProfile): void;
  toggleModal(visible: boolean): void;
  discardScan(): void;
  saveScan(userProfile: UserProfile): void;
  startScan(): void;
  saveAssessment(userProfile: UserProfile): any;
  redoBodyAssessment(userProfile: UserProfile): void;
  setAnswer(): void;
  getNextQuestion(): void;
  getPreviousQuestion(): void;
  showBodyTypes(): void;
  setProgress(progress: ScanProgress): void;
  updateBodyCompositionAndConditioning(composition: bodyComposition,
                                       athleteModeSelected: boolean, userProfile: UserProfile): any;
  continueToSetWeightTarget(userProfile: UserProfile): void;
  confirmRemoveScale(shouldRemoveScale: boolean): void;
}

class BodyscaleScanModalComponent extends React.PureComponent<IProps, any> {
  constructor(props: IProps) {
    super(props);
  }

  public closeModal = () => {
    this.discardScanAndClose();
  }

  public saveBodyScan = () => {
    const { toggleModal, saveScan, userProfile } = this.props;
    saveScan(userProfile);
  }

  public continueToSetWeightTarget = () => {
    this.props.continueToSetWeightTarget(this.props.userProfile);
  }

  public discardScanAndClose = () => {
    const { toggleModal, discardScan } = this.props;
    discardScan();
    toggleModal(false);
  }

  public getNextQuestionAndSaveWhenDone = async () => {
    const {
      getNextQuestion,
      setProgress,
      saveAssessment,
      currentQuestion,
      userProfile,
    } = this.props;
    getNextQuestion();
    if (currentQuestion.isLastQuestion) {
      await saveAssessment(userProfile);
      setProgress(ScanProgress.AssessmentResults);
    }
  }

  public redoBodyAssessment = () => {
    const { redoBodyAssessment, userProfile } = this.props;
    redoBodyAssessment(userProfile);
  }

  public setBodyCompositionTypeAndStartScan = async (compositionType,
                                                     athleteModeSelected: boolean, userProfile: UserProfile) => {
    const { updateBodyCompositionAndConditioning, setProgress } = this.props;
    await updateBodyCompositionAndConditioning(compositionType, athleteModeSelected, userProfile);
    this.goBack();
  }

  public openSmartScaleEcomm = async () => {
    const url = await FirebaseRemoteConfig.getRemoteConfigValue(
      RemoteConfigVariables.smart_scale_shop_url,
    );
    openInAppWebViewerInBackground(url);
  }

  public goBack() {
    const { from, setProgress } = this.props;
    setProgress(from);
  }

  public openFaq = () => {
    const { FAQ_SCALE_SCIENCE } = getUrls();
    openInAppWebViewerInBackground(FAQ_SCALE_SCIENCE);
  }

  public removeScale = () => {
    const { setProgress } = this.props;
    setProgress(ScanProgress.RemoveScale);
  }

  public render() {
    const {
      startScan,
      scaleScanStats,
      progress,
      phase,
      error,
      userProfile,
      isSaving,
      metrics,
      questionsAndAnswers,
      currentQuestion,
      currentQuestionAnswers,
      questionStep,
      setAnswer,
      getPreviousQuestion,
      composition,
      showBodyTypes,
      athleteMode,
      startScanSetup,
      isScanContinuingFromWeightTarget,
      isReviewingTargetAndSavingScan,
      confirmRemoveScale,
      isRemovingBodyScale,
    } = this.props;
    let renderBodyscaleComp;
    switch (progress) {
      case ScanProgress.BeforeScan:
        renderBodyscaleComp = (
          <BodyscaleStartScan
            startScan={startScan}
            bodyComposition={composition}
            showBodyGroups={showBodyTypes}
            athleteMode={athleteMode}
            removeScale={() => this.removeScale()}
          />
        );
        break;
      case ScanProgress.Onboarding:
        renderBodyscaleComp = (
          <BodyscaleOnboarding
            startBodyAssessment={this.redoBodyAssessment}
            openSmartScaleEcomm={() => this.openSmartScaleEcomm()}
            goToFaq={this.openFaq}
          />
        );
        break;
      case ScanProgress.OverrideBodyType:
        renderBodyscaleComp = (
          <BodyscaleBodyTypes
            bodyCompositionType={composition}
            athleteMode={athleteMode}
            updateBodyCompositionAndConditioning=
            {(compositionType, athleteModeSelected) =>
              this.setBodyCompositionTypeAndStartScan(compositionType, athleteModeSelected, userProfile)
            }
            redoBodyAssessment={this.redoBodyAssessment}
            back={() => this.goBack()}
            isSaving={isSaving}
          />
        );
        break;
      case ScanProgress.CaptureMetrics:
        renderBodyscaleComp = (
          <BodyscaleAssessment
            metrics={metrics}
            questionsAndAnswers={questionsAndAnswers}
            currentQuestion={currentQuestion}
            currentQuestionAnswers={currentQuestionAnswers}
            questionStep={questionStep}
            setAnswer={setAnswer}
            getNextQuestion={this.getNextQuestionAndSaveWhenDone}
            getPreviousQuestion={getPreviousQuestion}
            userProfile={userProfile}
            closeModal={this.closeModal}
            isSaving={isSaving}
          />
        );
        break;
      case ScanProgress.AssessmentResults:
        renderBodyscaleComp = (
          <BodyscaleAssessmentSummary
            composition={composition}
            showBodyGroups={showBodyTypes}
            startScan={() => {
              startScanSetup(userProfile);
              startScan();
            }}
            athleteMode={athleteMode}
          />
        );
        break;
      case ScanProgress.Complete:
        renderBodyscaleComp = (
          <BodyscaleScanSummary
            bodyScanStats={scaleScanStats}
            saveBodyScan={this.saveBodyScan}
            redoBodyAssessment={this.redoBodyAssessment}
            continueToSetWeightTarget={this.continueToSetWeightTarget}
            longTermGoal={userProfile.longTermGoal}
            isScanContinuingFromWeightTarget={isScanContinuingFromWeightTarget}
            isReviewingTargetAndSavingScan={isReviewingTargetAndSavingScan}
          />
        );
        break;
      case ScanProgress.Error:
        renderBodyscaleComp = (
          <BodyscaleScanError error={error} startScan={startScan} />
        );
        break;
      case ScanProgress.Inprogress:
      case ScanProgress.Notstarted:
        renderBodyscaleComp = (
          <BodyscaleScanProgress
            bodyComposition={composition}
            progress={progress}
            phase={phase}
            showBodyGroups={showBodyTypes}
           />
        );
        break;
        case ScanProgress.RemoveScale: {
          renderBodyscaleComp = (
            <RemoveBodyScale
              confirmRemoveScale={confirmRemoveScale}
              isRemovingBodyScale={isRemovingBodyScale}
             />
          );
        }
    }

    return (
        <View style={styles.container}>
          {renderBodyscaleComp}
        </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  modalContainer: {
    backgroundColor: Theme.WHITE,
  },
  headerTextStyle: {
    zIndex: 1,
    paddingBottom: defaultScale(10),
  },
  assessmentHeaderTextStyle: {
    ...Theme.textStyle.bodyCopyMed,
    zIndex: 1,
    paddingBottom: defaultScale(10),
    fontSize: defaultScale(20),
  },
  subtitleTextStyle: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK_MEDIUM,
  },
  containerStyle: {
    height: '100vh',
  },
  progressStyle: {
    marginTop: defaultScale(12),
    marginBottom: defaultScale(10),
    marginRight: defaultScale(20),
  },
});

const mapStateToProps = (state: any, ownProps) => {
  const { bodyscanModalVisible } = state.ui;
  const { isBodyScaleScanStarted, progress, phase, isSaving, error, from } =
    state.bodyscale.view;
  const { bodyAssessment, metrics } = state.bodyscale.data;
  const { questionsAndAnswers, currentQuestion, currentQuestionAnswers, composition } =
    bodyAssessment;
  const questionStep = getQuestionStep(state);
  const {user: {userProfile}} = ownProps;
  return {
    bodyscanModalVisible,
    isBodyScaleScanStarted,
    scaleScanStats: bodyScanStatsGaugeSelector(state),
    isScanContinuingFromWeightTarget: selectIsScanContinuingFromWeightTarget(state),
    progress,
    athleteMode: selectAthleteMode(state),
    phase,
    isSaving,
    error,
    metrics,
    questionsAndAnswers,
    currentQuestion,
    currentQuestionAnswers,
    questionStep,
    composition,
    from,
    userProfile,
    isReviewingTargetAndSavingScan: selectIsReviewingTargetAndSavingScan(state),
    isRemovingBodyScale: selectIsRemovingBodyScale(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    console.log,
    new AuthTokenBuilder(),
    GoogleAnalytics,
  );
  const userProfileService = ownProps.user.userProfileService;
  return {
    saveScan: (userProfile) => dispatch(saveBodyScaleScan(userServiceClient, userProfile)),
    discardScan: () => dispatch(discardBodyScaleScan()),
    toggleModal: (visible: boolean) => dispatch(toggleBodyScanModal(visible)),
    startScan: () => dispatch(startBodyScaleScan(userServiceClient)),
    startScanSetup: (userProfile: UserProfile) => dispatch(startBodyScaleScanSetup(userProfile)),
    saveAssessment: async (userProfile: UserProfile) =>
      dispatch(saveBodyAssessment(userServiceClient, userProfile, userProfileService)),
    redoBodyAssessment: (userProfile: UserProfile) =>
      dispatch(doBodyAssessment(userProfile)),
    showBodyTypes: () => dispatch(showBodyTypeOptions()),
    setAnswer: (answer) => dispatch(setBodyAssessmentAnswer(answer)),
    getNextQuestion: () => dispatch(getNextBodyAssessmentQuestion()),
    getPreviousQuestion: () => dispatch(getPreviousBodyAssessmentQuestion()),
    setProgress: (progress) => dispatch(setScanProgress(progress)),
    updateBodyCompositionAndConditioning: (composition, athleteModeSelected: boolean, userProfile) =>
      dispatch(overrideBodyCompositionAndConditioning(userServiceClient, composition,
        athleteModeSelected, userProfile)),
    continueToSetWeightTarget: (userProfile: UserProfile) =>
      dispatch(continueToSetYourWeightTargetButtonPressed(
        userServiceClient, userProfile, userProfile.PALValue, userProfileService)),
    confirmRemoveScale: (shouldRemoveScale: boolean) => {
      dispatch(removeBodyScale(shouldRemoveScale, userServiceClient, userProfileService));
    },
  };
};

const BodyscaleScanModal = UserDataConsumer(connect(
  mapStateToProps,
  mapDispatchToProps,
)(BodyscaleScanModalComponent));
export default translate(['bodyscan'], { wait: true })(BodyscaleScanModal);
